import { ajvResolver } from '@hookform/resolvers/ajv'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IRecipeDTO } from 'data/dtos/automation/i-recipe-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import RecipeSchema, { IRecipeFormData } from 'schema/automation/recipe'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const RecipeForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IRecipeFormData>({
    resolver: ajvResolver(RecipeSchema),
    defaultValues: {
      recipeName: '',
      defaultParams: '',
      connectors: '',
      workflow: '',
      isInactive: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/recipes/${id}`)
        .then((response) => {
          const { data } = response.data

          const recipeResult = {
            recipeName: data.recipeName,
            defaultParams: data.defaultParams,
            connectors: data.connectors,
            workflow: data.workflow,
            isInactive: data.isInactive,
          }

          return recipeResult
        })
        .then((recipeResult: IRecipeDTO) => {
          reset(recipeResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: IRecipeDTO) => {
    setIsLoading(true)

    const payLoad: IRecipeDTO = {
      recipeName: data.recipeName,
      defaultParams: data.defaultParams,
      connectors: data.connectors,
      workflow: data.workflow,
      isInactive: data.isInactive,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/recipes`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/recipes')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/recipes', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Receitas'
        subTitle='Dados coletados sobre as receitas de automação.'
      />
      <Form.Body>
        <Form.Input
          label='Receita'
          error={!!errors.recipeName}
          errorMessage={errors?.recipeName?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={100}
          name='recipeName'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Parâmetros Padrão'
          error={!!errors.defaultParams}
          errorMessage={errors?.defaultParams?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='defaultParams'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Conectores'
          error={!!errors.connectors}
          errorMessage={errors?.connectors?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='connectors'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Fluxo'
          error={!!errors.workflow}
          errorMessage={errors?.workflow?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='workflow'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.CheckBox
          label='Inativo'
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='isInactive'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default RecipeForm
